import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  AnalyticsAudience,
  fetchAnalyticsPreference,
  saveAnalyticsPreference,
} from '../../api/analyticsPreferenceApi';
import './AnalyticsPreference.css';

const AnalyticsPreference: React.FC<{ audience: AnalyticsAudience }> = ({ audience }) => {
  const descriptionId = useId();
  const [allowed, setAllowed] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState(true);
  const [error, setError] = useState(false);
  const [saved, setSaved] = useState(false);
  const retryValue = useRef<boolean | undefined>();
  const request = useRef<AbortController | null>(null);

  const syncPreference = useCallback(async (nextAllowed?: boolean) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    retryValue.current = nextAllowed;
    setPending(true);
    setError(false);
    setSaved(false);
    try {
      const result = nextAllowed === undefined
        ? await fetchAnalyticsPreference(audience, controller.signal)
        : await saveAnalyticsPreference(audience, nextAllowed, controller.signal);
      if (controller.signal.aborted) return;
      setAllowed(result.allowed);
      setConfirmed(true);
      setSaved(nextAllowed !== undefined);
    } catch {
      if (!controller.signal.aborted) setError(true);
    } finally {
      if (!controller.signal.aborted) setPending(false);
    }
  }, [audience]);

  useEffect(() => {
    setAllowed(false);
    setConfirmed(false);
    void syncPreference();
    return () => request.current?.abort();
  }, [syncPreference]);

  return (
    <div className="analytics-preference">
      <label className="analytics-preference__label">
        <input
          type="checkbox"
          checked={allowed}
          disabled={pending || !confirmed}
          aria-describedby={descriptionId}
          onChange={(event) => void syncPreference(event.target.checked)}
        />
        <span>Share product usage analytics <small>(optional)</small></span>
      </label>
      <p id={descriptionId}>Help improve Luma by sharing recording and playback milestones with PostHog. Audio, story text and names are not included. You can turn this off anytime.</p>
      {pending && <p role="status">{confirmed ? 'Saving your choice…' : 'Checking your analytics preference…'}</p>}
      {saved && <p role="status">Analytics sharing is {allowed ? 'on' : 'off'}.</p>}
      {error && (
        <div role="alert">
          <p>{confirmed ? 'We couldn’t confirm this change. Your last confirmed choice is shown.' : 'We couldn’t load your analytics preference.'} You can continue using Luma.</p>
          <button type="button" onClick={() => void syncPreference(retryValue.current)}>Retry analytics preference</button>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPreference;
